import { ComponentEntity, Entity } from '@backstage/catalog-model';

export type Type =
    | 'System'
    | 'Provider'
    | 'Group'
    | 'Service'
    | 'Database'
    | 'Website'
    | 'Job'
    | 'Workflow'
    | 'Library'
    | 'Resource';

export type C4DiagramType =
    | 'context'
    | 'container'
    | 'component'
    | 'deployment'
    | 'landscape';

export function toType(entity: Entity): Type {
    if (entity.kind.toLowerCase() === 'system') {
        return 'System';
    } else if (entity.kind.toLowerCase() === 'provider') {
        return 'Provider';
    } else if (entity.kind.toLowerCase() === 'group') {
        return 'Group';
    } else if (entity.kind.toLowerCase() === 'component') {
        const componentEntity = entity as ComponentEntity;
        if (componentEntity.spec.type.toLowerCase() === 'website') {
            return 'Website';
        } else if (componentEntity.spec.type.toLowerCase() === 'service') {
            return 'Service';
        } else if (componentEntity.spec.type.toLowerCase() === 'database') {
            return 'Database';
        } else if (componentEntity.spec.type.toLowerCase() === 'job') {
            return 'Job';
        } else if (componentEntity.spec.type.toLowerCase() === 'workflow') {
            return 'Workflow';
        } else if (componentEntity.spec.type.toLowerCase() === 'library') {
            return 'Library';
        }
    }
    return 'Service';
}

export function toDescription(entity: Entity): string {
    return entity.metadata.description
        ? entity.metadata.description
        : 'Description not available';
}

export function toLink(
    entity: Entity,
    diagramType?: C4DiagramType,
): string | undefined {
    const link = `/catalog/${entity.metadata.namespace}/${entity.kind.toLowerCase()}/${entity.metadata.name}`;
    if (diagramType) {
        return `${link}/${diagramType}`;
    }
    return link;
}

export function toText(entity: Entity): string {
    return entity?.kind === 'Product'
        ? `Product: ${entity.metadata.title}`
        : entity.metadata.title!;
}

export function toC4DiagramDeploymentNode(
    entity: Entity,
): C4DiagramDeploymentNode {
    return {
        id: entity.metadata.name,
        technology: false,
        text: toText(entity),
        link: toLink(entity),
        entities: [],
    };
}

export function toC4DiagramEntity(
    entity: Entity,
    diagramType?: C4DiagramType,
    context?: boolean,
    external?: boolean,
): C4DiagramEntity {
    return {
        id: entity.metadata.name,
        external: !!entity.metadata.tags?.includes('external') || !!external,
        context: context || false,
        text: entity.metadata.title!,
        type: toType(entity),
        link: toLink(entity, diagramType),
        description: toDescription(entity),
        owner: entity.spec?.owner as string,
        lifecycle: entity.spec?.lifecycle as string,
        location: entity.metadata.annotations
            ? entity.metadata.annotations['backstage.io/managed-by-location']
            : undefined,
    };
}

export interface C4DiagramModel {
    type: C4DiagramType;
    context?: C4DiagramEntity;
    entities: C4DiagramEntity[];
    relations: C4DiagramRelation[];
    deploymentNodes?: C4DiagramDeploymentNode[];
}

export interface C4DiagramRelation {
    id: string;
    source: string;
    target: string;
    text?: string;
}

export interface C4DiagramDeploymentNode {
    id: string;
    text: string;
    technology: boolean;
    entities?: C4DiagramDeploymentNodeEntity[];
    link?: string;
    deploymentNodes?: C4DiagramDeploymentNode[];
    parent?: string;
}

export interface C4DiagramDeploymentNodeEntity {
    id: string;
    entity: C4DiagramEntity;
}

export interface C4DiagramEntity {
    external: boolean;
    context: boolean;
    generated?: boolean;
    type: Type;
    id: string;
    text: string;
    description: string;
    owner?: string;
    lifecycle?: string;
    system?: string;
    location?: string;
    entities?: C4DiagramEntity[];
    link?: string;
}

export interface MergeCommands {
    commands: Command[];
    branch: string;
    message: string;
}

export interface Command {
    location: string;
    action: 'new' | 'delete' | 'change';
    relation?: {
        id: string;
        source: string;
        sourceType: Type;
        target: string;
        targetType: Type;
        description?: string;
    };
    container?: {
        id: string;
        type: Type;
        system?: string;
        title?: string;
        owner?: string;
        lifecycle?: string;
        description?: string;
    };
    technology?: {
        id: string;
        text?: string;
    };
}
